import React, { useCallback, useEffect, useRef, useState } from 'react';

// Газобетон: количество автоматически округляется до полных поддонов, поэтому
// onChange возвращает уже пересчитанное (округлённое) значение. Если биндить
// инпут напрямую к нему — при наборе цифра мгновенно «прыгает» к полному
// поддону, и поле невозможно отредактировать.
//
// Решение: пока поле в фокусе — показываем локальный черновик (то, что реально
// набрал пользователь), а к каноническому значению возвращаемся после blur.
//
// onChange при этом откладывается: каждое нажатие меняет `products`, а за ним
// идёт полный круг пересчёта — сравнение производств, способы доставки, сборка
// расчёта заказа. Набирая «1100», менеджер запускал четыре таких круга подряд,
// и расчёт заказа успевал спрятаться и появиться на каждый символ. Поэтому
// наружу уходит только последнее значение серии: набор идёт по локальному
// черновику, расчёт стартует, когда ввод остановился. Уход с поля отправляет
// значение сразу — ждать задержку там незачем.
const CHANGE_DELAY_MS = 400;

function GazNumberInput({ value, onChange, allowDecimal = false, maxDecimals = 6 }) {
  const [draft, setDraft] = useState(value ?? '');
  const [focused, setFocused] = useState(false);

  // Значение, которое ещё не ушло наружу. Держим в ссылке: его читают таймер и
  // blur, а перерисовывать поле на него не нужно — оно уже показано черновиком.
  const pendingValueRef = useRef(null);
  const timerRef = useRef(null);
  // onChange приходит новой функцией на каждый рендер родителя, а таймер живёт
  // между рендерами и должен вызвать самую свежую.
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!focused) {
      setDraft(value ?? '');
    }
  }, [value, focused]);

  const cancelPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flushPending = useCallback(() => {
    cancelPending();
    if (pendingValueRef.current === null) {
      return;
    }
    const valueToSend = pendingValueRef.current;
    pendingValueRef.current = null;
    onChangeRef.current?.(valueToSend);
  }, [cancelPending]);

  // Строка удалена или заменена — отложенное значение уже некуда отправлять.
  useEffect(() => cancelPending, [cancelPending]);

  const scheduleChange = useCallback(
    (nextValue) => {
      pendingValueRef.current = nextValue;
      cancelPending();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flushPending();
      }, CHANGE_DELAY_MS);
    },
    [cancelPending, flushPending]
  );

  const handleChange = (e) => {
    const inputValue = e.target.value;

    if (allowDecimal) {
      const normalizedValue = inputValue.replace(',', '.');
      const decimalPattern = new RegExp(`^\\d{0,10}([.]\\d{0,${maxDecimals}})?$`);
      if (normalizedValue === '' || decimalPattern.test(normalizedValue)) {
        setDraft(normalizedValue);
        scheduleChange(normalizedValue);
      }
      return;
    }

    if (inputValue === '' || /^[0-9]{0,10}$/.test(inputValue)) {
      setDraft(inputValue);
      scheduleChange(inputValue);
    }
  };

  return (
    <input
      type="text"
      value={focused ? draft : (value ?? '')}
      onChange={handleChange}
      onFocus={() => {
        setFocused(true);
        setDraft(value ?? '');
      }}
      onBlur={() => {
        // Порядок важен: сначала отдаём набранное, потом снимаем признак фокуса.
        // React 19 сведёт оба состояния в один коммит, и поле не мигнёт прежним
        // значением, пока расчёт не вернул новое.
        flushPending();
        setFocused(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          flushPending();
        }
      }}
      className="form-control fs-6"
    />
  );
}

export default GazNumberInput;
